import { Button, Pagination, PropertyFilter, Table, TableProps } from '@amzn/awsui-components-react'
import {
    SETUP_HEALTH_ORG_FILTERING_PROPERTIES,
    PROPERTY_FILTER_I18NSTRING,
} from '../../reusable/UseCollectionUtil'
import { getMatchesCountText } from '../../reusable/AllocationUtils'
import { useState } from 'react'
import { useCollection } from '@amzn/awsui-collection-hooks'
import { ITEM_TYPES } from '../../../Constant'
import EmptyState from '../../reusable/EmptyState'
import StatusInfoPopover from '../../reusable/StatusInfoPopover'
import headcountSourceLink from '../../reusable/HeadcountSourceLink'
import SetupHealthTableHeader from './SetupHealthTableHeader'
import { getGroupControlColumns, getTeamControlColumns } from './SetupHealthUtil'

const SetupHealthOrgTable = (props) => {
    const { businessEntity, orgs, selectedOrgs, onSelectedOrgsChange, isLoading, isGroupLoading } =
        props

    const columns: TableProps.ColumnDefinition<any>[] = [
        {
            id: 'org_name',
            header: 'Organization',
            cell: (e) => e.org_name,
            sortingField: 'org_name',
        },
        {
            id: 'number_of_groups',
            header: (
                <StatusInfoPopover
                    title={
                        <>
                            Falcon <br></br> Groups
                        </>
                    }
                    popoverContent='Groups with 0 teams are not displayed in the Org preview, but are included in the count.'
                />
            ),
            cell: (e) => e.number_of_groups['falcon'] || 0,
            sortingField: 'number_of_groups',
            sortingComparator: (a, b) =>
                (a.number_of_groups['falcon'] || 0) - (b.number_of_groups['falcon'] || 0),
        },
        ...getGroupControlColumns(ITEM_TYPES.ORG),
        ...getTeamControlColumns(ITEM_TYPES.ORG),
        {
            id: 'hr_permission_group',
            header: (
                <StatusInfoPopover
                    title='Control Source'
                    popoverContent='The organization control headcount permission group. Click to launch TEAM permission in a new tab.'
                />
            ),
            cell: (e) => headcountSourceLink(e.hr_permission_group, e.hr_permission_group_name),
            sortingField: 'hr_permission_group_name',
        },
    ]

    const [preferences] = useState({ pageSize: 50 })
    const {
        items,
        filteredItemsCount,
        collectionProps,
        propertyFilterProps,
        paginationProps,
        actions,
    } = useCollection(orgs, {
        propertyFiltering: {
            filteringProperties: SETUP_HEALTH_ORG_FILTERING_PROPERTIES,
            empty: (
                <EmptyState
                    title='No orgs'
                    subtitle='No orgs to display.'
                    action={<Button href='/orgs'>Set up Org</Button>}
                />
            ),
            noMatch: (
                <EmptyState
                    title='No matches'
                    subtitle='We can’t find a match.'
                    action={<Button onClick={() => actions.setFiltering('')}>Clear filter</Button>}
                />
            ),
        },
        pagination: { pageSize: preferences.pageSize },
        sorting: {
            defaultState: {
                sortingColumn: columns[0],
            },
        },
        selection: {},
    })

    return (
        <Table
            {...collectionProps}
            selectionType='single'
            selectedItems={selectedOrgs}
            onSelectionChange={({ detail }) => {
                if (detail.selectedItems === selectedOrgs) {
                    return
                }

                onSelectedOrgsChange(detail.selectedItems)
            }}
            isItemDisabled={() => isGroupLoading}
            columnDefinitions={columns}
            items={items}
            loading={isLoading}
            loadingText='Loading Orgs'
            wrapLines
            pagination={<Pagination {...paginationProps} />}
            filter={
                <PropertyFilter
                    i18nStrings={PROPERTY_FILTER_I18NSTRING}
                    {...propertyFilterProps}
                    countText={getMatchesCountText(filteredItemsCount)}
                />
            }
            header={
                businessEntity && (
                    <SetupHealthTableHeader
                        headerName={businessEntity.business_entity_name}
                        control={businessEntity.control_headcount_sum}
                        expected={businessEntity.expected_team_sum}
                        variance={businessEntity.variance_team}
                        hrPermissionGroup={businessEntity.hr_permission_group}
                        hrPermissionGroupName={businessEntity.hr_permission_group_name}
                    />
                )
            }
        />
    )
}

export default SetupHealthOrgTable
